import React, { useState } from 'react';
import { Box, Button } from '@material-ui/core';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { FiPlus, FiSave, FiTrash2 } from 'react-icons/fi';

import { ButtonTitleWithLoading } from '@vyce/core/src/components/ButtonTitleWithLoading';

import { AppAccordion } from '@vyce/core/src/components/AppAccordion';
import { TimeAndAttendanceLocation } from '@vyce/core/src/types';

import { StaffItem } from '../hooks/useStaffSearch';
import { SnapshotFormValue } from '../types';
import { SnapshotFormFields } from './SnapshotFormFields';

interface Props {
  fieldArrayName: string;
  locations: TimeAndAttendanceLocation[];
  snapshotTitle: string;
  addButtonLabel: string;
  defaultSnapshot: Record<string, unknown> | SnapshotFormValue;
  staff?: StaffItem[];
  setStaffSubstring?: (substring: string) => void;
  boldClassName?: string;
  onSave?: () => void;
  onDelete?: (index: number) => void;
  onToggleActive?: (index: number) => void;
  saving?: boolean;
  togglingNotificationId?: string | null;
}

/** The list of individual Snapshot Emails inside the "Snapshot Email" settings card. */
export const SnapshotList: React.FC<Props> = props => {
  const { fieldArrayName, locations, staff = [], setStaffSubstring, boldClassName, defaultSnapshot } = props;
  // -1 — все снапшоты свёрнуты, пока админ сам не раскроет нужный
  const [expandedSnapshotIndex, setExpandedSnapshotIndex] = useState(-1);

  const { control, watch } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldArrayName,
  });

  const handleAccordionChange = (clickedIndex: number) => {
    setExpandedSnapshotIndex(prev => (prev === clickedIndex ? -1 : clickedIndex));
  };

  const removeSnapshot = (index: number) => {
    props.onDelete?.(index);
    remove(index);
    setExpandedSnapshotIndex(prev => {
      if (prev === index) return -1;
      return prev > index ? prev - 1 : prev;
    });
  };

  const addSnapshot = () => {
    const newIndex = fields.length;
    append({ ...defaultSnapshot } as any);
    setExpandedSnapshotIndex(newIndex);
  };

  return (
    <Box display="flex" flexDirection="column" gridGap={12}>
      {fields.map((field, index) => {
        const snapshot = watch(`${fieldArrayName}.${index}`) as SnapshotFormValue | undefined;
        const isFromDb = !!snapshot?.notificationId;
        const isActive = snapshot?.isActive ?? true;
        const isInactive = isFromDb && !isActive;
        const title = snapshot?.name ?? `${props.snapshotTitle} ${index + 1}`;

        return (
          <Box key={field.id} style={{ opacity: isInactive ? 0.5 : 1 }}>
            <AppAccordion
              title={title}
              outsideControl
              outsideExpanded={expandedSnapshotIndex === index}
              panel={index}
              onOutsideChange={handleAccordionChange}
              renderSummaryRightContent={() => (
                <Box display="flex" alignItems="center" gridGap={8}>
                  {props.onSave && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      style={{ width: 100 }}
                      startIcon={<FiSave size={16} />}
                      onClick={e => {
                        e.stopPropagation();
                        props.onSave?.();
                      }}
                      disabled={
                        expandedSnapshotIndex !== index || (props.saving && expandedSnapshotIndex === index)
                      }
                      aria-label="Save snapshot"
                    >
                      <ButtonTitleWithLoading
                        title="Save"
                        loaderVariant="primary"
                        loading={!!props.saving && expandedSnapshotIndex === index}
                      />
                    </Button>
                  )}
                  {isFromDb ? (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ minWidth: 100 }}
                      onClick={e => {
                        e.stopPropagation();
                        props.onToggleActive?.(index);
                      }}
                      disabled={props.togglingNotificationId === snapshot?.notificationId}
                      aria-label={isActive ? 'Deactivate snapshot' : 'Activate snapshot'}
                    >
                      <ButtonTitleWithLoading
                        title={isActive ? 'Deactivate' : 'Activate'}
                        loaderVariant="primary"
                        loading={props.togglingNotificationId === snapshot?.notificationId}
                      />
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      style={{ minWidth: 100 }}
                      startIcon={<FiTrash2 size={16} />}
                      onClick={e => {
                        e.stopPropagation();
                        removeSnapshot(index);
                      }}
                      aria-label="Delete snapshot"
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              )}
              renderDetailsBlock={() => (
                <SnapshotFormFields
                  fieldPrefix={`${fieldArrayName}.${index}`}
                  locations={locations}
                  boldClassName={boldClassName}
                  staff={staff}
                  setStaffSubstring={setStaffSubstring}
                />
              )}
            />
          </Box>
        );
      })}

      <Box display="flex" alignItems="center" gridGap={16}>
        <Button variant="contained" color="primary" startIcon={<FiPlus size={16} />} onClick={addSnapshot}>
          {props.addButtonLabel}
        </Button>
      </Box>
    </Box>
  );
};
